import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import {
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  RefObject,
  Suspense,
  useRef,
} from 'react';
import { CardControlModal } from '../../../../../components/TimetableGrid/CardControl/CardControlModal';
import { CardEditModal } from '../../../../../components/TimetableGrid/CardEditModal/CardEditModal';
import { CardInfo } from '../../../../../components/TimetableGrid/CardInfo/CardInfo';
import { CardPreview } from '../../../../../components/TimetableGrid/CardPreview/CardPreview';
import { ConflictListModal } from '../../../../../components/TimetableGrid/ConflictListModal/ConflictListModal';
import { ConflictModal } from '../../../../../components/TimetableGrid/ConflictModal/ConflictModal';
import { ConstraintModal } from '../../../../../components/TimetableGrid/ConstraintModal/ConstraintModal';
import { GridActionArea } from '../../../../../components/TimetableGrid/GridActionArea/GridActionArea';
import { GridMenuBar } from '../../../../../components/TimetableGrid/GridMenuBar/GridMenuBar';
import { MathplanInfoModal } from '../../../../../components/TimetableGrid/InfoModal/MathplanInfoModal';
import { PreviewMap } from '../../../../../components/TimetableGrid/PreviewMap/PreviewMap';
import { SolverWizardModal } from '../../../../../components/TimetableGrid/SolverWizard/SolverWizardModal';
import { TestModal } from '../../../../../components/TimetableGrid/TestModal/TestModal';
import { TimefoldAnalyzeModal } from '../../../../../components/TimetableGrid/TimefoldAnalyzeModal';
import { TimetableGrid } from '../../../../../components/TimetableGrid/TimetableGrid';
import {
  TimetablePreviewFirst,
  TimetablePreviewSecond,
} from '../../../../../components/TimetableGrid/TimetablePreview/TimetablePreview';
import { useTimetableStore } from '../../../../../components/TimetableGrid/TimetableProvider';
import styles from './Board.module.scss';

export const Board = observer(() => {
  const store = useTimetableStore();
  const isSplit = store.boardLayout === 'split';

  // Regler 1: zwischen Auswahl-Bereich und Board (Container = board-body).
  // split → Breite (%) über X, stacked → Höhe (px) über Y.
  const containerRef = useRef<HTMLDivElement>(null);
  const startResizeBoard = (event: ReactMouseEvent) => {
    event.preventDefault();
    const onMove = (move: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (isSplit) {
        store.setBoardSplitWidth(((move.clientX - rect.left) / rect.width) * 100);
      } else {
        store.setBoardTopHeight(move.clientY - rect.top);
      }
    };
    attachDrag(onMove);
  };
  const onBoardDividerKeyDown = (event: ReactKeyboardEvent) => {
    if (isSplit) {
      if (event.key === 'ArrowLeft') store.setBoardSplitWidth(store.boardSplitWidth - 2);
      if (event.key === 'ArrowRight') store.setBoardSplitWidth(store.boardSplitWidth + 2);
    } else {
      if (event.key === 'ArrowUp') store.setBoardTopHeight(store.boardTopHeight - 16);
      if (event.key === 'ArrowDown') store.setBoardTopHeight(store.boardTopHeight + 16);
    }
  };

  // Regler 2: zwischen CardPicker und CardInfo (Container = panels).
  // stacked → nebeneinander, Breite (%) über X; split → übereinander, Höhe (%) über Y.
  const panelsRef = useRef<HTMLDivElement>(null);
  const startResizePanels = (event: ReactMouseEvent) => {
    event.preventDefault();
    const onMove = (move: MouseEvent) => {
      const rect = panelsRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (isSplit) {
        store.setBoardPickerHeight(((move.clientY - rect.top) / rect.height) * 100);
      } else {
        store.setBoardPickerWidth(((move.clientX - rect.left) / rect.width) * 100);
      }
    };
    attachDrag(onMove);
  };
  const onPanelsDividerKeyDown = (event: ReactKeyboardEvent) => {
    if (isSplit) {
      if (event.key === 'ArrowUp') store.setBoardPickerHeight(store.boardPickerHeight - 2);
      if (event.key === 'ArrowDown') store.setBoardPickerHeight(store.boardPickerHeight + 2);
    } else {
      if (event.key === 'ArrowLeft') store.setBoardPickerWidth(store.boardPickerWidth - 2);
      if (event.key === 'ArrowRight') store.setBoardPickerWidth(store.boardPickerWidth + 2);
    }
  };

  // Regler 3: zwischen (optionaler) PreviewMap und CardInfo. Größe der Map als % des Rest-Bereichs
  // (hinter dem Picker), gemessen ab dessen Kante — CardInfo füllt den verbleibenden Rest.
  const restRef = useRef<HTMLDivElement>(null);
  const previewMapRef = useRef<HTMLDivElement>(null);
  const teacherPreviewRef = useRef<HTMLDivElement>(null);
  const roomPreviewRef = useRef<HTMLDivElement>(null);
  const startResizePreviewMap = (event: ReactMouseEvent) => {
    event.preventDefault();
    const onMove = (move: MouseEvent) => {
      const restRect = restRef.current?.getBoundingClientRect();
      if (!restRect) return;
      if (isSplit) {
        store.setBoardPreviewMapHeight(((move.clientY - restRect.top) / restRect.height) * 100);
      } else {
        store.setBoardPreviewMapWidth(((move.clientX - restRect.left) / restRect.width) * 100);
      }
    };
    attachDrag(onMove);
  };
  const onPreviewMapDividerKeyDown = (event: ReactKeyboardEvent) => {
    if (isSplit) {
      if (event.key === 'ArrowUp') store.setBoardPreviewMapHeight(store.boardPreviewMapHeight - 2);
      if (event.key === 'ArrowDown') store.setBoardPreviewMapHeight(store.boardPreviewMapHeight + 2);
    } else {
      if (event.key === 'ArrowLeft') store.setBoardPreviewMapWidth(store.boardPreviewMapWidth - 2);
      if (event.key === 'ArrowRight') store.setBoardPreviewMapWidth(store.boardPreviewMapWidth + 2);
    }
  };

  // Regler 4 + 5: Lehrer- und Raum-Stundenplan sind zwei eigenständige Panels, jedes unabhängig
  // justierbar. Anders als bei der (immer ersten) PreviewMap wird hier ab der EIGENEN Panel-Kante
  // gemessen — sonst würde ein vorgelagertes Panel die %-Rechnung verfälschen.
  const startResizeRestPanel =
    (panelRef: RefObject<HTMLDivElement | null>, setWidth: (v: number) => void, setHeight: (v: number) => void) =>
    (event: ReactMouseEvent) => {
      event.preventDefault();
      const onMove = (move: MouseEvent) => {
        const restRect = restRef.current?.getBoundingClientRect();
        const panelRect = panelRef.current?.getBoundingClientRect();
        if (!restRect || !panelRect) return;
        if (isSplit) {
          setHeight(((move.clientY - panelRect.top) / restRect.height) * 100);
        } else {
          setWidth(((move.clientX - panelRect.left) / restRect.width) * 100);
        }
      };
      attachDrag(onMove);
    };
  const stepRestPanel =
    (getWidth: () => number, setWidth: (v: number) => void, getHeight: () => number, setHeight: (v: number) => void) =>
    (event: ReactKeyboardEvent) => {
      if (isSplit) {
        if (event.key === 'ArrowUp') setHeight(getHeight() - 2);
        if (event.key === 'ArrowDown') setHeight(getHeight() + 2);
      } else {
        if (event.key === 'ArrowLeft') setWidth(getWidth() - 2);
        if (event.key === 'ArrowRight') setWidth(getWidth() + 2);
      }
    };
  const startResizeTeacherPreview = startResizeRestPanel(
    teacherPreviewRef,
    (v) => store.setBoardTimetablePreviewTeachersWidth(v),
    (v) => store.setBoardTimetablePreviewTeachersHeight(v),
  );
  const onTeacherPreviewDividerKeyDown = stepRestPanel(
    () => store.boardTimetablePreviewTeachersWidth,
    (v) => store.setBoardTimetablePreviewTeachersWidth(v),
    () => store.boardTimetablePreviewTeachersHeight,
    (v) => store.setBoardTimetablePreviewTeachersHeight(v),
  );
  const startResizeRoomPreview = startResizeRestPanel(
    roomPreviewRef,
    (v) => store.setBoardTimetablePreviewRoomsWidth(v),
    (v) => store.setBoardTimetablePreviewRoomsHeight(v),
  );
  const onRoomPreviewDividerKeyDown = stepRestPanel(
    () => store.boardTimetablePreviewRoomsWidth,
    (v) => store.setBoardTimetablePreviewRoomsWidth(v),
    () => store.boardTimetablePreviewRoomsHeight,
    (v) => store.setBoardTimetablePreviewRoomsHeight(v),
  );

  // Der gesamte Auswahl-Bereich (nicht nur der CardPicker) ist Ablage: Klick mit Karte in der
  // Hand legt sie zurück in den Stapel. `data-card-stack` hält zugleich den Outside-Click-Handler
  // (CardPreview) hier ab, damit die Karte nicht auf ihre alte Position zurückspringt.
  const onPickerPanelClick = () => {
    if (!store.pickedCard) return;
    if (store.context === 'rooms') {
      store.discardPickedCardToRoomsStack();
    } else {
      store.discardPickedCardToStack();
    }
  };

  // Panels hinter dem Picker in Reihenfolge. Ist die CardInfo aus, füllt das LETZTE sichtbare
  // Panel den frei gewordenen Platz (flex-grow statt fixe Größe) und verliert seine nachlaufende
  // Trennlinie — sonst bliebe dort eine „tote" Trennlinie samt Leerraum stehen.
  type RestPanel = {
    key: string;
    ref: RefObject<HTMLDivElement | null>;
    content: ReactNode;
    widthPct: number;
    heightPct: number;
    startResize: (event: ReactMouseEvent) => void;
    onKeyDown: (event: ReactKeyboardEvent) => void;
  };
  const restPanels: RestPanel[] = [];
  if (store.showPreviewMap) {
    restPanels.push({
      key: 'preview-map',
      ref: previewMapRef,
      content: <PreviewMap />,
      widthPct: store.boardPreviewMapWidth,
      heightPct: store.boardPreviewMapHeight,
      startResize: startResizePreviewMap,
      onKeyDown: onPreviewMapDividerKeyDown,
    });
  }
  if (store.showTimetablePreview) {
    restPanels.push({
      key: 'timetable-first',
      ref: teacherPreviewRef,
      content: <TimetablePreviewFirst />,
      widthPct: store.boardTimetablePreviewTeachersWidth,
      heightPct: store.boardTimetablePreviewTeachersHeight,
      startResize: startResizeTeacherPreview,
      onKeyDown: onTeacherPreviewDividerKeyDown,
    });
    restPanels.push({
      key: 'timetable-second',
      ref: roomPreviewRef,
      content: <TimetablePreviewSecond />,
      widthPct: store.boardTimetablePreviewRoomsWidth,
      heightPct: store.boardTimetablePreviewRoomsHeight,
      startResize: startResizeRoomPreview,
      onKeyDown: onRoomPreviewDividerKeyDown,
    });
  }

  return (
    <div className='board-wrapper' id='timetable-grid'>
      <div className={styles.surface}>
        <div className={styles['menu-section']}>
          <GridMenuBar />
        </div>
        <div ref={containerRef} className={classNames(styles['board-body'], styles[isSplit ? 'split' : 'stacked'])}>
          <div
            ref={panelsRef}
            className={styles.panels}
            style={isSplit ? { width: `${store.boardSplitWidth}%` } : { height: `${store.boardTopHeight}px` }}
          >
            <div
              className={styles['picker-panel']}
              style={isSplit ? { height: `${store.boardPickerHeight}%` } : { width: `${store.boardPickerWidth}%` }}
              data-card-stack=''
              onClick={onPickerPanelClick}
            >
              <GridActionArea />
            </div>
            <div
              className={classNames(styles.divider, styles[isSplit ? 'horizontal' : 'vertical'])}
              role='separator'
              aria-orientation={isSplit ? 'horizontal' : 'vertical'}
              tabIndex={0}
              onMouseDown={startResizePanels}
              onKeyDown={onPanelsDividerKeyDown}
            />
            {/* Rest-Bereich hinter dem Picker: optionale PreviewMap (feste Größe) + CardInfo (füllt
                den Rest). So kann die Map die CardInfo nie verdecken – beide teilen sich den Platz. */}
            <div ref={restRef} className={styles.rest}>
              {restPanels.map((panel, index) => {
                // Ohne CardInfo wächst das letzte Panel in den freien Platz und lässt seine
                // nachlaufende Trennlinie weg.
                const grow = !store.showCardInfo && index === restPanels.length - 1;
                return (
                  <Fragment key={panel.key}>
                    <div
                      ref={panel.ref}
                      className={classNames(styles['preview-map-panel'], { [styles['grow-panel']]: grow })}
                      style={
                        grow ? undefined : isSplit ? { height: `${panel.heightPct}%` } : { width: `${panel.widthPct}%` }
                      }
                    >
                      {panel.content}
                    </div>
                    {!grow && (
                      <div
                        className={classNames(styles.divider, styles[isSplit ? 'horizontal' : 'vertical'])}
                        role='separator'
                        aria-orientation={isSplit ? 'horizontal' : 'vertical'}
                        tabIndex={0}
                        onMouseDown={panel.startResize}
                        onKeyDown={panel.onKeyDown}
                      />
                    )}
                  </Fragment>
                );
              })}
              {store.showCardInfo && (
                <div className={styles['info-panel']}>
                  <CardInfo />
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames(styles.divider, styles[isSplit ? 'vertical' : 'horizontal'])}
            role='separator'
            aria-orientation={isSplit ? 'vertical' : 'horizontal'}
            tabIndex={0}
            onMouseDown={startResizeBoard}
            onKeyDown={onBoardDividerKeyDown}
          />
          <div className={styles['board-panel']}>
            <TimetableGrid />
          </div>
        </div>
      </div>
      <CardPreview />
      <Suspense fallback={<></>}>
        <ConstraintModal />
      </Suspense>
      <CardControlModal />
      <TestModal />
      <TimefoldAnalyzeModal />
      <SolverWizardModal />
      <MathplanInfoModal />
      <CardEditModal />
      <ConflictModal />
      <ConflictListModal />
    </div>
  );
});

Board.displayName = 'Board';

// Gemeinsame Drag-Mechanik: mousemove/mouseup am document, Textauswahl während des Ziehens aus.
function attachDrag(onMove: (event: MouseEvent) => void) {
  const onUp = () => {
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}
